/**
 * 사이트 디자인 데이터 평면화 유틸리티 (병렬 처리 최적화)
 *
 * 깊은 중첩 구조를 평면화하여 사용하기 쉽게 만들고,
 * 각 레벨별로 데이터에 접근할 수 있도록 합니다.
 *
 * 🚀 성능 최적화: Promise.all()을 사용한 병렬 처리
 *
 * Domain 필드(seq, displayNo)는 panelHierarchyMappers 경계에서 추가.
 * API 호환 id/stringId/arrayId/moduleId 별칭은 유지.
 */
import {
  arrayFromApi,
  moduleFromApi,
  stringFromApi
} from '@/features/panel-planning/utils/panelHierarchyMappers.js';

/**
 * 사이트 디자인 데이터를 평면화합니다. (병렬 처리 최적화)
 * @param {Object} siteDesign - 원본 사이트 디자인 데이터
 * @returns {Promise<Object>} 평면화된 데이터 구조
 */
export async function flattenSiteDesignData(siteDesign) {
  if (!siteDesign) {
    return {
      flattened: [],
      bySection: new Map(),
      byArray: new Map(),
      byString: new Map(),
      byModule: new Map(),
      hierarchy: {}
    };
  }

  const flattened = [];
  const bySection = new Map();
  const byArray = new Map();
  const byString = new Map();
  const byModule = new Map();

  // 계층 구조 정보
  const hierarchy = {
    siteId: siteDesign.id,
    siteName: siteDesign.name,
    sections: []
  };

  // 사이트 기본 정보
  const siteInfo = {
    id: siteDesign.id,
    siteId: siteDesign.siteId,
    name: siteDesign.name,
    description: siteDesign.description,
    address: siteDesign.address,
    location: siteDesign.location,
    level: 'site',
    parentId: null,
    children: []
  };

  flattened.push(siteInfo);
  hierarchy.children = [];

  // 🚀 병렬 처리: 각 섹션을 독립적으로 처리
  if (siteDesign.solarSections) {
    const sectionPromises = siteDesign.solarSections.map(async (section, sectionIndex) => {
      return await processSection(
        section,
        sectionIndex,
        siteDesign.id,
        flattened,
        bySection,
        byArray,
        byString,
        byModule
      );
    });

    const processedSections = await Promise.all(sectionPromises);

    // 결과를 hierarchy에 추가
    processedSections.forEach(sectionInfo => {
      hierarchy.children.push(sectionInfo);
    });
  }

  return {
    flattened,
    bySection,
    byArray,
    byString,
    byModule,
    hierarchy
  };
}

/**
 * 🚀 섹션을 병렬로 처리하는 함수
 */
async function processSection(
  section,
  sectionIndex,
  siteId,
  flattened,
  bySection,
  byArray,
  byString,
  byModule
) {
  const sectionInfo = {
    id: section.id,
    sectionId: section.sectionId,
    name: section.name,
    level: 'section',
    parentId: siteId,
    sectionIndex,
    children: [],
    solarArrays: []
  };

  flattened.push(sectionInfo);
  bySection.set(section.id, sectionInfo);

  // 🚀 병렬 처리: 각 배열을 독립적으로 처리
  if (section.solarArrays) {
    const arrayPromises = section.solarArrays.map(async (array, arrayIndex) => {
      return await processArray(
        array,
        arrayIndex,
        section,
        sectionIndex,
        flattened,
        bySection,
        byArray,
        byString,
        byModule
      );
    });

    const processedArrays = await Promise.all(arrayPromises);

    // 결과를 sectionInfo에 추가
    processedArrays.forEach(arrayInfo => {
      sectionInfo.children.push(arrayInfo);
      sectionInfo.solarArrays.push(arrayInfo);
    });
  }

  return sectionInfo;
}

/**
 * 🚀 배열을 병렬로 처리하는 함수
 */
async function processArray(
  array,
  arrayIndex,
  section,
  sectionIndex,
  flattened,
  bySection,
  byArray,
  byString,
  byModule
) {
  const arrayDomain = arrayFromApi(array);
  const arrayInfo = {
    id: arrayDomain.seq ?? array.id,
    arrayId: arrayDomain.displayNo ?? array.arrayId,
    seq: arrayDomain.seq,
    displayNo: arrayDomain.displayNo,
    name: array.name,
    shapeGeog: array.shapeGeog,
    level: 'array',
    parentId: section.id,
    sectionIndex,
    arrayIndex,
    children: [],
    solarStrings: []
  };

  flattened.push(arrayInfo);
  byArray.set(array.id, arrayInfo);

  // 🚀 병렬 처리: 각 스트링을 독립적으로 처리
  if (array.solarStrings) {
    const stringPromises = array.solarStrings.map(async (string, stringIndex) => {
      return await processString(
        string,
        stringIndex,
        array,
        sectionIndex,
        arrayIndex,
        flattened,
        bySection,
        byArray,
        byString,
        byModule
      );
    });

    const processedStrings = await Promise.all(stringPromises);

    // 결과를 arrayInfo에 추가
    processedStrings.forEach(stringInfo => {
      arrayInfo.children.push(stringInfo);
      arrayInfo.solarStrings.push(stringInfo);
    });
  }

  return arrayInfo;
}

/**
 * 🚀 스트링을 병렬로 처리하는 함수
 */
async function processString(
  string,
  stringIndex,
  array,
  sectionIndex,
  arrayIndex,
  flattened,
  bySection,
  byArray,
  byString,
  byModule
) {
  const stringDomain = stringFromApi(string);
  const stringInfo = {
    id: stringDomain.seq ?? string.id,
    stringId: stringDomain.displayNo ?? string.stringId,
    seq: stringDomain.seq,
    displayNo: stringDomain.displayNo,
    name: string.name,
    rowNum: string.rowNum,
    colNum: string.colNum,
    description: string.description,
    shapeGeog:
      string.shapeGeog ||
      (string.shapeCoordinates ? { coordinates: [string.shapeCoordinates] } : undefined),
    level: 'string',
    parentId: array.id,
    sectionIndex,
    arrayIndex,
    stringIndex,
    children: [],
    solarModules: []
  };

  flattened.push(stringInfo);
  byString.set(string.id, stringInfo);

  // 🚀 병렬 처리: 각 모듈을 독립적으로 처리
  if (string.solarModules) {
    const modulePromises = string.solarModules.map(async (module, moduleIndex) => {
      return await processModule(
        module,
        moduleIndex,
        string,
        sectionIndex,
        arrayIndex,
        stringIndex,
        flattened,
        bySection,
        byArray,
        byString,
        byModule
      );
    });

    const processedModules = await Promise.all(modulePromises);

    // 결과를 stringInfo에 추가
    processedModules.forEach(moduleInfo => {
      stringInfo.children.push(moduleInfo);
      stringInfo.solarModules.push(moduleInfo);
    });
  }

  return stringInfo;
}

/**
 * 🚀 모듈을 처리하는 함수
 */
async function processModule(
  module,
  moduleIndex,
  string,
  sectionIndex,
  arrayIndex,
  stringIndex,
  flattened,
  bySection,
  byArray,
  byString,
  byModule
) {
  const moduleDomain = moduleFromApi(module);
  const moduleInfo = {
    id: moduleDomain.seq ?? module.id,
    moduleId: moduleDomain.displayNo ?? module.moduleId,
    seq: moduleDomain.seq,
    displayNo: moduleDomain.displayNo,
    shapeGeog:
      module.shapeGeog ||
      (module.shapeCoordinates ? { coordinates: [module.shapeCoordinates] } : undefined),
    centerCoord: module.centerCoord,
    level: 'module',
    parentId: string.id,
    sectionIndex,
    arrayIndex,
    stringIndex,
    moduleIndex,
    children: []
  };

  flattened.push(moduleInfo);
  byModule.set(module.id, moduleInfo);

  return moduleInfo;
}

/**
 * 🚀 동기 버전 (기존 호환성을 위해 유지)
 * @param {Object} siteDesign - 원본 사이트 디자인 데이터
 * @returns {Object} 평면화된 데이터 구조
 */
export function flattenSiteDesignDataSync(siteDesign) {
  if (!siteDesign) {
    return {
      flattened: [],
      bySection: new Map(),
      byArray: new Map(),
      byString: new Map(),
      byModule: new Map(),
      hierarchy: {}
    };
  }

  const flattened = [];
  const bySection = new Map();
  const byArray = new Map();
  const byString = new Map();
  const byModule = new Map();

  // 계층 구조 정보
  const hierarchy = {
    siteId: siteDesign.id,
    siteName: siteDesign.name,
    sections: []
  };

  // 사이트 기본 정보
  const siteInfo = {
    id: siteDesign.id,
    siteId: siteDesign.siteId,
    name: siteDesign.name,
    description: siteDesign.description,
    address: siteDesign.address,
    location: siteDesign.location,
    level: 'site',
    parentId: null,
    children: []
  };

  flattened.push(siteInfo);
  hierarchy.children = [];

  // 기존 방식 (동기 처리)
  if (siteDesign.solarSections) {
    siteDesign.solarSections.forEach((section, sectionIndex) => {
      const sectionInfo = {
        id: section.id,
        sectionId: section.sectionId,
        name: section.name,
        level: 'section',
        parentId: siteDesign.id,
        sectionIndex,
        children: [],
        solarArrays: []
      };

      flattened.push(sectionInfo);
      bySection.set(section.id, sectionInfo);
      hierarchy.children.push(sectionInfo);

      // Solar Arrays 처리
      if (section.solarArrays) {
        section.solarArrays.forEach((array, arrayIndex) => {
          const arrayInfo = {
            id: array.id,
            arrayId: array.arrayId,
            name: array.name,
            shapeGeog: array.shapeGeog,
            level: 'array',
            parentId: section.id,
            sectionIndex,
            arrayIndex,
            children: [],
            solarStrings: []
          };

          flattened.push(arrayInfo);
          byArray.set(array.id, arrayInfo);
          sectionInfo.children.push(arrayInfo);
          sectionInfo.solarArrays.push(arrayInfo);

          // Solar Strings 처리
          if (array.solarStrings) {
            array.solarStrings.forEach((string, stringIndex) => {
              const stringInfo = {
                id: string.id,
                stringId: string.stringId,
                name: string.name,
                rowNum: string.rowNum,
                colNum: string.colNum,
                description: string.description,
                shapeGeog:
                  string.shapeGeog ||
                  (string.shapeCoordinates
                    ? { coordinates: [string.shapeCoordinates] }
                    : undefined),
                level: 'string',
                parentId: array.id,
                sectionIndex,
                arrayIndex,
                stringIndex,
                children: [],
                solarModules: []
              };

              flattened.push(stringInfo);
              byString.set(string.id, stringInfo);
              arrayInfo.children.push(stringInfo);
              arrayInfo.solarStrings.push(stringInfo);

              // Solar Modules 처리
              if (string.solarModules) {
                string.solarModules.forEach((module, moduleIndex) => {
                  const moduleInfo = {
                    id: module.id,
                    moduleId: module.moduleId,
                    shapeGeog: module.shapeGeog,
                    centerCoord: module.centerCoord,
                    level: 'module',
                    parentId: string.id,
                    sectionIndex,
                    arrayIndex,
                    stringIndex,
                    moduleIndex,
                    children: []
                  };

                  flattened.push(moduleInfo);
                  byModule.set(module.id, moduleInfo);
                  stringInfo.children.push(moduleInfo);
                  stringInfo.solarModules.push(moduleInfo);
                });
              }
            });
          }
        });
      }
    });
  }

  return {
    flattened,
    bySection,
    byArray,
    byString,
    byModule,
    hierarchy
  };
}

/**
 * 특정 레벨의 데이터만 추출합니다.
 * @param {Object} flattenedData - 평면화된 데이터
 * @param {string} level - 추출할 레벨 ('section', 'array', 'string', 'module')
 * @returns {Array} 해당 레벨의 데이터 배열
 */
export function getDataByLevel(flattenedData, level) {
  if (!flattenedData?.flattened) return [];
  return flattenedData.flattened.filter(item => item.level === level);
}

/**
 * 특정 ID의 데이터와 그 하위 데이터를 모두 가져옵니다.
 * @param {Object} flattenedData - 평면화된 데이터
 * @param {string|number} id - 검색할 ID
 * @param {string} level - 검색할 레벨 ('section', 'array', 'string', 'module')
 * @returns {Object|null} 해당 데이터와 하위 데이터
 */
export function getDataWithChildren(flattenedData, id, level) {
  if (!flattenedData?.flattened) return null;

  const target = flattenedData.flattened.find(item => item.id === id && item.level === level);

  if (!target) return null;

  // 하위 데이터 재귀적으로 수집
  const collectChildren = item => {
    const result = { ...item };
    if (item.children && item.children.length > 0) {
      result.children = item.children.map(child => collectChildren(child));
    }
    return result;
  };

  return collectChildren(target);
}

/**
 * 특정 조건에 맞는 데이터를 검색합니다.
 * @param {Object} flattenedData - 평면화된 데이터
 * @param {Function} predicate - 검색 조건 함수
 * @returns {Array} 조건에 맞는 데이터 배열
 */
export function searchData(flattenedData, predicate) {
  if (!flattenedData?.flattened) return [];
  return flattenedData.flattened.filter(predicate);
}

/**
 * 데이터의 통계 정보를 계산합니다.
 * @param {Object} flattenedData - 평면화된 데이터
 * @returns {Object} 통계 정보
 */
export function getDataStatistics(flattenedData) {
  if (!flattenedData?.flattened) {
    return {
      totalItems: 0,
      byLevel: {},
      maxDepth: 0
    };
  }

  const byLevel = {};
  let maxDepth = 0;

  flattenedData.flattened.forEach(item => {
    // 레벨별 개수 계산
    if (!byLevel[item.level]) {
      byLevel[item.level] = 0;
    }
    byLevel[item.level]++;

    // 최대 깊이 계산
    const depth = [item.sectionIndex, item.arrayIndex, item.stringIndex, item.moduleIndex].filter(
      index => index !== undefined
    ).length;
    maxDepth = Math.max(maxDepth, depth);
  });

  return {
    totalItems: flattenedData.flattened.length,
    byLevel,
    maxDepth
  };
}

/**
 * 평면화된 데이터를 원래 계층 구조로 복원합니다.
 * @param {Object} flattenedData - 평면화된 데이터
 * @returns {Object} 원래 계층 구조
 */
export function restoreHierarchy(flattenedData) {
  if (!flattenedData?.hierarchy) return null;

  const restoreChildren = item => {
    if (!item.children || item.children.length === 0) {
      return item;
    }

    const restored = { ...item };
    restored.children = item.children.map(child => restoreChildren(child));

    // children 속성 제거 (원본 구조와 동일하게)
    delete restored.children;

    return restored;
  };

  return restoreChildren(flattenedData.hierarchy);
}

/**
 * 데이터 변경사항을 추적하는 함수
 * @param {Object} oldData - 이전 데이터
 * @param {Object} newData - 새로운 데이터
 * @returns {Object} 변경사항 정보
 */
export function trackDataChanges(oldData, newData) {
  const oldFlattened = flattenSiteDesignDataSync(oldData);
  const newFlattened = flattenSiteDesignDataSync(newData);

  const changes = {
    added: [],
    removed: [],
    modified: [],
    unchanged: []
  };

  // 추가된 항목 찾기
  newFlattened.flattened.forEach(newItem => {
    const oldItem = oldFlattened.flattened.find(
      item => item.id === newItem.id && item.level === newItem.level
    );

    if (!oldItem) {
      changes.added.push(newItem);
    } else if (JSON.stringify(oldItem) !== JSON.stringify(newItem)) {
      changes.modified.push({
        old: oldItem,
        new: newItem
      });
    } else {
      changes.unchanged.push(newItem);
    }
  });

  // 제거된 항목 찾기
  oldFlattened.flattened.forEach(oldItem => {
    const newItem = newFlattened.flattened.find(
      item => item.id === oldItem.id && item.level === oldItem.level
    );

    if (!newItem) {
      changes.removed.push(oldItem);
    }
  });

  return changes;
}

function cloneFlatRecord(record) {
  if (!record || typeof record !== 'object') {
    return record;
  }
  const copy = { ...record };
  if (Array.isArray(copy.solarStrings)) {
    copy.solarStrings = copy.solarStrings.map(item => ({ ...item }));
  }
  if (Array.isArray(copy.solarModules)) {
    copy.solarModules = copy.solarModules.map(item => ({ ...item }));
  }
  if (Array.isArray(copy.children)) {
    copy.children = copy.children.map(item => ({ ...item }));
  }
  if (Array.isArray(copy.solarArrays)) {
    copy.solarArrays = copy.solarArrays.map(item => ({ ...item }));
  }
  return copy;
}

function cloneFlatMap(source) {
  const map = new Map();
  if (!source) {
    return map;
  }
  for (const [key, value] of source.entries()) {
    map.set(key, cloneFlatRecord(value));
  }
  return map;
}

/** Vuex commit용 스냅샷 — 로컬 편집·패치 전 컴포넌트 쪽 복제에 사용 (store는 참조 공유) */
export function cloneFlattenedSiteDesign(flattened) {
  if (!flattened) {
    return null;
  }
  return {
    flattened: (flattened.flattened || []).map(cloneFlatRecord),
    bySection: cloneFlatMap(flattened.bySection),
    byArray: cloneFlatMap(flattened.byArray),
    byString: cloneFlatMap(flattened.byString),
    byModule: cloneFlatMap(flattened.byModule),
    hierarchy: flattened.hierarchy
      ? JSON.parse(JSON.stringify(flattened.hierarchy))
      : {}
  };
}
